import React, { useContext, useEffect, useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { theme } from "../../../../themes/MuiTheme";
import RejectPackageBtn from "../inspect-package-tools/RejectPackageBtn";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { OCLandInsightsContext } from "../../../../hooks/context/OcLandInsightsContext";
import { selectionDataContext } from "../../../../hooks/context/SelectionDataContext";
import CreateWorkOrder from "../../aim/buttons/CreateWorkOrder";
import {
  removeAllFromSelectionLayer,
  selectDataHandler,
} from "../../select-form/spatial-select/spatialSelectVM";
import { BoxApiUrl } from "../../../../apis/OcpwBoxDoc/OcpwBoxApi";
import axios from "axios";
import { omInspectionGisFieldsObject } from "../FieldObjects";

interface InspPackageReviewProps {
  activeWidget?: string;
  view?: __esri.MapView | __esri.SceneView;
  portal?: any;
}

  const pckgIdName = omInspectionGisFieldsObject.packageId.name;

const InspPackageReview: React.FC<InspPackageReviewProps> = ({
  view,
  portal,
}) => {
  const [channelPackages, setChannelPackages] = useState<string[]>([]);
  const [roadPackages, setRoadPackages] = useState<string[]>([]);
  const [selectedPkgId, setSelectedPkgId] = useState<string | null>(null);

  const ocLandInsightsDictionaryCtx: any = useContext(OCLandInsightsContext);
  const { channelInspections, roadInspections } =
    ocLandInsightsDictionaryCtx.data || {};

  const selectionDataCtx: any = useContext(selectionDataContext);
  const payload = selectionDataCtx.data;

  useEffect(() => {
    if (!view || !view.map.allLayers || !channelInspections || !roadInspections)
      return;

    const fetchPackages = async () => {
      const [channelPkgs, roadPkgs] = await Promise.all([
        getUniquePackages(channelInspections, view),
        getUniquePackages(roadInspections, view),
      ]);
      setChannelPackages(channelPkgs);
      setRoadPackages(roadPkgs);
    };

    fetchPackages();
  }, [view, payload]);

  const handlePackageClick = async (
    pkgId: string,
    layerId: string,
    isCurrentlySelected: boolean
  ) => {
    removeAllFromSelectionLayer(view.map.allLayers); // Deselecting the currently selected one
    if (!isCurrentlySelected) {
      const attributeObject = createPackageAttributeObject(
        layerId,
        view,
        pkgId
      );
      if (!attributeObject) return;
      await selectDataHandler(view, attributeObject, false);
    }
  };

  const [folderUrls, setFolderUrls] = useState<{ [pkg: string]: string }>({});


  useEffect(() => {
    const fetchFolderUrls = async (packages: string[]) => {
      const urls: { [pkg: string]: string } = {};
      const parentFolderId = "330175420648";
      try {
        // Fetch all folders/files in the parent folder once
        const response = await axios.get(`${BoxApiUrl}/folder-files?folderid=${parentFolderId}`);
        const items = response.data || [];
        packages.forEach((pkg) => {
          // Find a folder whose name matches the package name
          const match = items.find((item: any) => item.type === "folder" && item.name === pkg);
          urls[pkg] = match ? match.id : "";
        });
      } catch (error) {
        console.error("Error fetching folder files", error);
        packages.forEach((pkg) => {
          urls[pkg] = "";
        });
      }
      setFolderUrls(urls);
    };

    // Fetch for both channel and road packages
    fetchFolderUrls([...channelPackages, ...roadPackages]);
  }, [channelPackages, roadPackages]);

  useEffect(() => {
    if (
      selectedPkgId &&
      !channelPackages.includes(selectedPkgId) &&
      !roadPackages.includes(selectedPkgId)
    ) {
      setSelectedPkgId(null);
    }
  }, [channelPackages, roadPackages]);

  const renderPackageList = (
    packages: string[],
    label: string,
    layerId: string
  ) => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">
          {label} ({packages.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {packages.length === 0 ? (
          <Typography color="textSecondary">
            No {label.toLowerCase()} found.
          </Typography>
        ) : (
          <List disablePadding>
            {packages.map((pkg) => {
              // Use folderUrls from state
              const folderUrl = `https://ocgov.app.box.com/folder/${folderUrls[pkg]}`;
              // isChecked logic (add this if needed, otherwise remove)
              const isChecked = selectedPkgId === pkg;
              return (
                <Tooltip
                  key={pkg}
                  title={
                    <a
                      href={folderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.palette.secondary.main,
                        textDecoration: "underline",
                        fontSize: "1rem",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {pkg}
                    </a>
                  }
                  arrow
                  placement="right"
                >
                  <ListItem
                    button
                    onClick={() => {
                      setSelectedPkgId((prevSelectedPkgId) => {
                        const isCurrentlySelected = prevSelectedPkgId === pkg;
                        handlePackageClick(pkg, layerId, isCurrentlySelected);
                        return isCurrentlySelected ? null : pkg;
                      });
                    }}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      paddingLeft: 0,
                      paddingRight: 0,
                      minHeight: 32,
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                      style={{ margin: 0, padding: 0, marginRight: 4 }}
                    />
                    <ListItemText
                      primary={
                        <Typography
                          noWrap
                          variant="body2"
                          style={{ fontSize: "0.85rem" }}
                        >
                          {pkg}
                        </Typography>
                      }
                    />
                  </ListItem>
                </Tooltip>
              );
            })}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
  return (
    <div style={{ padding: "0.5rem", marginTop: "1rem" }}>
      {renderPackageList(channelPackages, "Flood Packages", channelInspections)}
      {renderPackageList(roadPackages, "Road Packages", roadInspections)}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          margin: "1rem 0",
        }}
      >
        <RejectPackageBtn
          view={view}
          payload={payload}
          disabled={!selectedPkgId}
        />
        <CreateWorkOrder payload={payload} portal={portal} view={view} />
      </div>
    </div>
  );
};

export default InspPackageReview;

// =============================
// Utilities
// =============================

export const getUniquePackages = async (
  layerId: string,
  view: __esri.MapView | __esri.SceneView
): Promise<string[]> => {
  console.log("packgae id name", pckgIdName);

  if (!view || !view.map.allLayers) return [];
  const layer = view.map.allLayers.find((l: any) => l.layerId === layerId);
  if (!layer || layer.type !== "feature") return [];
  const featureLayer = layer as FeatureLayer;
  if (!featureLayer.queryFeatures) return [];
  try {
    const result = await featureLayer.queryFeatures({
      where: `${pckgIdName} IS NOT NULL AND Status = 'Needs Review'`,
      outFields: [`"${pckgIdName}"`],
      returnGeometry: false,
    });
    const features = result.features || [];
    const unique = Array.from(
      new Set(features.map((f: any) => f.attributes?.[pckgIdName]).filter(Boolean))
    );
    return unique;
  } catch (err) {
    console.error(`Error querying ${layerId} layer:`, err);
    return [];
  }
};

const createPackageAttributeObject = (
  layerId: string,
  view: __esri.MapView | __esri.SceneView,
  packageName: string
) => {
  if (!view || !view.map.allLayers) return null;
  const layer = view.map.allLayers.find((l: any) => l.layerId === layerId);
  if (!layer || layer.type !== "feature") return null;
  const featureLayer = layer as FeatureLayer;
  return {
    layerId: featureLayer.id,
    queryString: `${pckgIdName} = '${packageName}'`,
  };
};
